import { useQuery } from '@apollo/client';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { EventSetting } from '@thro-types/events';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import QUERY from 'gql/queries/EventService/getEventSetting.gql';

import { GQLGuard } from 'app/components';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface Props {
  occupancy: number;
}

enum Color {
  RED = 'red',
  ORANGE = 'orange',
  GREEN = 'green',
  GREY = 'grey'
}

export const PercentageSpinner = ({ occupancy }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const query = useQuery(QUERY, {
    context: { clientName: 'federation' },
    fetchPolicy: 'cache-first'
  });

  if (!occupancy) {
    occupancy = 0;
  }

  const handleGetColor = (settings: EventSetting) => {
    if (occupancy < settings.occupancyGreen) return Color.GREY;
    else if (occupancy < settings.occupancyYellow) return Color.GREEN;
    else if (occupancy < settings.occupancyRed) return Color.ORANGE;
    else return Color.RED;
  };

  return (
    <GQLGuard
      query={query}
      onError={error => {
        dispatch(AlertActions.error(`${t(translations.pages.eventSettings.error.loading())}`));
        navigate('/', { replace: true });
        return null;
      }}
    >
      {(settings: { getEventSetting: EventSetting }) => (
        <LabeledInput label={t(translations.pages.events.create.occupancy())}>
          <Grid
            item
            sx={{
              position: 'relative',
              display: 'flex',
              justifySelf: 'flex-end',
              alignItems: 'center',
              textAlign: 'center'
            }}
            className={`event-card__circular-progress--${handleGetColor(settings.getEventSetting)}`}
          >
            <CircularProgress
              variant="determinate"
              value={Math.min(occupancy, 100)}
              thickness={7}
              size={50}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary">{`${Math.round(
                occupancy || 0
              )}%`}</Typography>
            </Box>
          </Grid>
        </LabeledInput>
      )}
    </GQLGuard>
  );
};
